import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft, Globe, Plane } from 'lucide-react';
import { transladoPage, siteInfo } from '../data/mockData';

const Translado = () => {
  // SEO Optimization
  React.useEffect(() => {
    document.title = 'Translado Funerário Nacional e Internacional | RJ 24h';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Translado funerário nacional e internacional. Assistência completa para traslado e repatriação. Atendimento 24 horas no Rio de Janeiro.';
  }, []);

  // Schema.org
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Translado Funerário",
    "name": "Translado Funerário Nacional e Internacional",
    "description": "Serviço de translado funerário nacional e internacional com assistência completa",
    "provider": {
      "@type": "FuneralHome",
      "name": "Santa Casa Assistencial",
      "telephone": "+55-21-4106-4022",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rio de Janeiro",
        "addressRegion": "RJ",
        "addressCountry": "BR"
      }
    },
    "areaServed": ["Brasil", "International"]
  };

  return (
    <div className="min-h-screen">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>

      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 188, 156, 0.85), rgba(20, 160, 133, 0.85)), url('${transladoPage.hero.image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="text-center px-4 max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">{transladoPage.hero.title}</h1>
          <p className="text-xl md:text-2xl text-teal-50">{transladoPage.hero.subtitle}</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-lime-600 hover:text-teal-600 transition-colors">
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Translado</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{transladoPage.title}</h2>
            <p className="text-xl text-gray-600">{transladoPage.subtitle}</p>
          </div>

          {/* Tipos de translado com ícones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mr-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Translado Nacional</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Transporte seguro para qualquer cidade do Brasil com toda documentação e logística necessária.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-lime-600 rounded-full flex items-center justify-center mr-4">
                  <Plane className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Translado Internacional</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Cuidamos de toda a documentação diplomática e consular para translado entre países.
              </p>
            </div>
          </div>

          {transladoPage.content.map((section, index) => (
            <div key={index} className="mb-12">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">{section.title}</h3>
              <p className="text-gray-700 leading-relaxed text-lg">{section.text}</p>
            </div>
          ))}

          {/* Benefícios */}
          <div className="bg-gray-900 text-white rounded-lg p-8 mb-12">
            <h3 className="text-2xl font-semibold mb-6">O que inclui nosso serviço</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {transladoPage.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-1" />
                  <span className="text-teal-50">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-teal-500 to-lime-600 text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-semibold mb-4">Precisa realizar um translado?</h3>
            <p className="text-teal-50 mb-6">
              Nossa equipe especializada está disponível 24 horas para orientar sobre todo o processo de translado nacional ou internacional.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center justify-center space-x-2 bg-white text-lime-600 hover:bg-gray-100 px-8 py-3 rounded-md text-lg font-medium transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                <span>Fale Conosco Agora</span>
              </a>
              <Link
                to="/contato"
                className="inline-block bg-gray-900 text-white hover:bg-gray-800 px-6 py-3 rounded-xl text-base font-bold transition-all duration-300"
              >
                Formulário de Contato
              </Link>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-lime-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para início
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serviços de Translado no Meier - Nacional e Internacional
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              A <strong>Santa Casa Serviço Funerário Imediato</strong> oferece <strong>serviços de translado no Meier</strong> e em todo o Rio de Janeiro, 
              tanto <strong>translado nacional</strong> quanto <strong>translado internacional</strong>. Com experiência e dedicar, 
              cuidamos de toda a documentação e logística necessária.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nossa <strong>funerária no Meier</strong> é especializada em <strong>translado funerário</strong>, oferecendo atendimento 24 horas 
              para famílias que precisam de <strong>translado nacional</strong> para outras cidades do Brasil ou <strong>translado internacional</strong> 
              para outros países.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Localizada no <strong>Meier, Rio de Janeiro</strong>, a Santa Casa Serviço Funerário Imediato resolve toda a burocracia de <strong>translado</strong>, 
              incluindo documentação sanitária, consular e coordenação com companhias aéreas. Entre em contato para mais informações sobre 
              <strong>translado no Meier</strong>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Translado;
